/**
 * core/i18n/index.ts — i18next initialisation + LanguageContext.
 *
 * Both locale bundles are imported at module load (not lazy-loaded) because
 * the JSON files are ~1700 lines each and the app already has them as static
 * assets. Lazy-loading can be added in a later slice if bundle-size analysis
 * warrants it.
 *
 * LanguageContext mirrors the ColorModeContext shape from theme/theme.ts:
 * a createContext default + typed interface + useLanguage hook. The provider
 * lives in Root.tsx so the whole tree is covered.
 *
 * Persistence: user's language choice is written to localStorage under the
 * key 'itow.language'. On first visit, 'en-us' is the default.
 * Browser language detection is explicitly excluded per the slice brief —
 * language is always user-selected.
 */
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import { createContext, useContext } from 'react';

import enUs from '../../assets/i18n/en-us.json';
import esMx from '../../assets/i18n/es-mx.json';

export const SUPPORTED_LANGUAGES = ['en-us', 'es-mx'] as const;
export type AppLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const LANGUAGE_STORAGE_KEY = 'itow.language';

function getInitialLanguage(): AppLanguage {
  const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return stored === 'es-mx' ? 'es-mx' : 'en-us';
}

i18next.use(initReactI18next).init({
  lng: getInitialLanguage(),
  fallbackLng: 'en-us',
  // Our locale codes use lowercase regions (`en-us`, `es-mx`) to match the
  // Angular app's i18n catalog filenames. By default i18next normalizes
  // BCP-47 codes to mixed case (`en-US`), which then doesn't match resource
  // keys — every t() call returns the literal key. Forcing lowercase keeps
  // the lookup bucket aligned with the resource bucket.
  lowerCaseLng: true,
  interpolation: {
    // React already escapes output — no need for i18next to double-escape.
    escapeValue: false,
  },
  // react-i18next v15 enables Suspense by default. With inline resources we
  // do not need it, and there is no Suspense boundary above useTranslation
  // consumers. Disable so components render synchronously off the resource
  // bundles defined below.
  react: {
    useSuspense: false,
  },
  resources: {
    'en-us': {
      translation: enUs,
    },
    'es-mx': {
      translation: esMx,
    },
  },
});

export default i18next;

// ─── Language context + hook ──────────────────────────────────────────────────

export interface LanguageContextValue {
  language: AppLanguage;
  toggleLanguage: () => void;
}

/**
 * LanguageContext is consumed by any component that needs the current locale or
 * needs to switch it (e.g. the AppBar language toggle button).
 *
 * The provider lives in Root.tsx so the entire app tree is covered.
 * Shape mirrors ColorModeContext in theme/theme.ts for consistency.
 */
export const LanguageContext = createContext<LanguageContextValue>({
  language: 'en-us',
  // Default no-op; replaced by real toggle once the provider mounts.
  toggleLanguage: () => undefined,
});

export function useLanguage(): LanguageContextValue {
  return useContext(LanguageContext);
}
